import { useEffect, useMemo, useState } from "react";
import { getPedidoById } from "../../../services/admin/pedidos_admin";
import { fileURL } from "../../../utils/fileURL";
import "./OrderViewModal.css";
import BarcodeLabelsModal from "../productos/BarcodeLabelsModal";
import { estadoDe, formatoUSD, textoDePago } from "./estadosPedido";

/**
 * Ficha de una orden de venta.
 *
 * Sigue el mismo orden que la ficha de producto: arriba de que orden se
 * trata y en que estado esta, despues los numeros que se miran de un
 * vistazo, y al final los datos de referencia y las lineas.
 */

/** "Aug 18, 2026". La fecha cruda ISO no se lee de corrido. */
const formatDate = (dt) => {
  if (!dt) return null;
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const money = (v) => formatoUSD(v);

// Sin unidad: ninguna linea de pedido tiene unidad_id cargada, asi que
// poner "lb" seria inventarlo. El peso por linea si la muestra cuando la
// trae.
const weightTotal = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Un dato de la cabecera: el valor en una pastilla y debajo que cosa es. */
const Chip = ({ valor, etiqueta }) => {
  if (valor === null || valor === undefined || valor === "") return null;

  return (
    <span className="omx-chip">
      <span className="omx-chip-valor">{valor}</span>
      <span className="omx-chip-label">{etiqueta}</span>
    </span>
  );
};

/** Una fila "etiqueta / valor" dentro de una tarjeta. */
const Dato = ({ label, valor }) => {
  const vacio =
    valor === null ||
    valor === undefined ||
    (typeof valor === "string" && !valor.trim());

  return (
    <div className="omx-dato">
      <dt className="omx-dato-label">{label}</dt>
      <dd className="omx-dato-valor">
        {vacio ? <span className="omx-muted">---</span> : valor}
      </dd>
    </div>
  );
};

/** Obtiene la etiqueta de unidad de un item (abreviatura o nombre desde unidades_peso) */
const getItemWeightUnit = (item) => {
  if (!item || typeof item !== "object") return "";
  const abrev =
    item.unidad_abreviatura ?? item.Unidad_abreviatura ?? item.unidadAbreviatura ?? "";
  const nombre =
    item.unidad_nombre ?? item.Unidad_nombre ?? item.unidadNombre ?? "";
  return (abrev && String(abrev).trim()) || (nombre && String(nombre).trim()) || "";
};

/** Peso del producto con unidad (ej. "10.50 lb"); usa getItemWeightUnit por si la API devuelve claves distintas */
const formatItemWeight = (item) => {
  const n = Number(item?.peso_producto);
  if (!Number.isFinite(n)) return "—";
  const unit = getItemWeightUnit(item);
  return unit ? `${n.toFixed(2)} ${unit}` : n.toFixed(2);
};

const paymentTone = (paid) => (paid ? "success" : "warn");

function OrderViewModal({ pedidoId, onClose }) {
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(false);

  // Va arriba con el resto de los hooks: mas abajo hay un return temprano
  // (if (!pedidoId) return null) y un hook despues de eso se ejecuta unas
  // veces si y otras no, que es justo lo que React no permite.
  const [verEtiquetas, setVerEtiquetas] = useState(false);

  useEffect(() => {
    if (!pedidoId) return;

    let alive = true;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);

    (async () => {
      try {
        setLoading(true);
        const data = await getPedidoById(pedidoId);
        if (!alive) return;
        setPedido(data || null);
      } catch (e) {
        console.error("Error loading order:", e);
        if (alive) setPedido(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [pedidoId, onClose]);

  const customerName = useMemo(() => {
    const full = `${pedido?.nombre_cliente || ""} ${pedido?.apellido_cliente || ""}`.trim();
    return full || "—";
  }, [pedido?.nombre_cliente, pedido?.apellido_cliente]);

  const esEmpresa = pedido?.tipo_cliente === "comercial";

  const phoneText = useMemo(() => {
    const code = String(pedido?.codigo_telefono || "").trim();
    const phone = String(pedido?.telefono || "").trim();
    const joined = `${code} ${phone}`.trim();
    return joined || "—";
  }, [pedido?.codigo_telefono, pedido?.telefono]);

  const closeOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!pedidoId) return null;

  const est = estadoDe(pedido);
  const payText = textoDePago(pedido);
  const paid = payText === "Paid";

  const items = Array.isArray(pedido?.items)
    ? pedido.items
    : Array.isArray(pedido?.detalles)
    ? pedido.detalles
    : [];

  /**
   * Las lineas del pedido en la forma que espera el modal de etiquetas.
   *
   * La cantidad sale de la linea (lo comprado), no del stock. El resto sale
   * del producto: el nombre de la linea es el del momento de la compra, pero
   * el numero de parte, el serial, la condicion y el certificado son de la
   * pieza.
   *
   * Cada etiqueta lleva ademas el numero de orden y el PO. Sin eso, una
   * etiqueta despegada o dos ordenes empacandose a la vez no se distinguen:
   * el P/N dice que pieza es, no a que pedido pertenece.
   *
   * Calculo normal y no useMemo: esto vive despues del return temprano, y
   * ahi un hook no puede ir. Mapear unas pocas lineas no cuesta nada.
   */
  const productosParaEtiquetas = items.map((d, i) => ({
    producto_id: d.producto_id ?? d.pedido_detalles_id,
    nombre: d.nombre_producto,
    no_parte: d.no_parte,
    numero_serie: d.numero_serie,
    codigo_barra: d.codigo_barra,
    cantidad: Number(d.cantidad) || 0,
    condicion_nombre: d.condicion_nombre,
    certificado_nombre: d.certificado_nombre,
    pedido_id: pedidoId,
    no_po: pedido?.no_po,
    // Para el "Item 2 of 5" que le dice al que empaca si el envio esta
    // completo. Es la posicion en la orden, no el id de la linea.
    linea: i + 1,
    lineas: items.length,
  }));

  const conCodigoDeBarras = productosParaEtiquetas.filter(
    (p) => p.codigo_barra
  ).length;

  // Mismo formato que el resto de las cifras de la ficha.
  const fmtCore = (v) => money(v);

  const calcCoreLine = (d) => {
    const backendTotal = Number(d?.core_charge_total);
    if (Number.isFinite(backendTotal)) return backendTotal;
    const ccUnit = Number(d?.core_charge_unit ?? d?.core_charge ?? 0);
    const qty = Number(d?.cantidad);
    if (!Number.isFinite(ccUnit) || !Number.isFinite(qty)) return null;
    return ccUnit * qty;
  };

  const getCoreUnit = (d) => Number(d?.core_charge_unit ?? d?.core_charge ?? 0);

  return (
    <div className="omx-overlay" onMouseDown={closeOverlay}>
      <div
        className="omx-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Sales order details"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ---------- Cabecera: de que orden se trata ---------- */}
        <div className="omx-header">
          <div className="omx-header-left">
            <h2 className="omx-title">Sales Order #{pedidoId}</h2>

            {/* Los datos con los que se identifica la orden, con el mismo
                tratamiento de pastilla que la lista y la ficha de producto:
                quien viene de la lista encuentra la misma forma aca. */}
            <div className="omx-chips">
              <Chip valor={pedido?.no_po} etiqueta="PO number" />
              <Chip valor={customerName} etiqueta="Customer" />
              <Chip valor={formatDate(pedido?.fecha_creado)} etiqueta="Created" />

              <span className={`omx-pill omx-pill--${est.tono}`}>
                {loading ? "Loading" : est.texto}
              </span>

              <span className={`omx-pill omx-pill--${paymentTone(paid)}`}>
                {loading ? "Loading" : payText}
              </span>
            </div>
          </div>

          <button className="omx-close" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>

        <div className="omx-body">
          {/* ---------- Los tres numeros que se miran primero ---------- */}
          <div className="omx-resumen">
            <div className="omx-kpi">
              <span className="omx-kpi-label">Invoice total</span>
              <span className="omx-kpi-valor">
                {pedido ? money(pedido.total_factura) : "—"}
              </span>
              <span className="omx-kpi-nota">
                {items.length === 1 ? "1 line" : `${items.length} lines`}
              </span>
            </div>

            <div className="omx-kpi">
              <span className="omx-kpi-label">Core charge total</span>
              <span className="omx-kpi-valor">
                {pedido ? money(pedido.core_charge_total) : "—"}
              </span>
              <span className="omx-kpi-nota">
                {Number(pedido?.core_charge_total) > 0
                  ? "Refundable on return"
                  : "No core charge"}
              </span>
            </div>

            <div className="omx-kpi">
              <span className="omx-kpi-label">Total weight</span>
              <span className="omx-kpi-valor">
                {pedido ? weightTotal(pedido.peso_total) : "—"}
              </span>
              {/* "UPS" solo, debajo de un numero, se lee como si fuera la
                  unidad del peso. */}
              <span className="omx-kpi-nota">
                {pedido?.nombre_metodo_envio
                  ? `Shipping via ${pedido.nombre_metodo_envio}`
                  : "No shipping method"}
              </span>
            </div>
          </div>

          {/* Cuatro tarjetas parejas y no una barra lateral: la de antes
              juntaba los totales, las fechas y el boton de cerrar en una
              columna altisima, y al lado quedaba medio modal en blanco. */}
          <div className="omx-columnas">
            <div className="omx-card">
              <div className="omx-card-head">
                <span className="omx-card-title">Customer</span>
              </div>

              {/* Sin "Customer ID": es la llave de la tabla, no un dato
                  que alguien use para atender un pedido. */}
              <dl className="omx-datos">
                <Dato label="Name" valor={customerName} />
                <Dato label="Email" valor={pedido?.correo} />
                <Dato label="Phone" valor={phoneText} />
              </dl>

              {/* Cuando la cuenta es de una empresa, a quien se le factura
                  no es quien compro. Sin esto habia que salir a la ficha
                  del cliente para saber a nombre de quien emitirla. */}
              {esEmpresa ? (
                <>
                  <div className="omx-card-head omx-card-head--sub">
                    <span className="omx-card-title">Bill to</span>
                  </div>

                  <dl className="omx-datos">
                    <Dato
                      label="Legal name"
                      valor={pedido?.razon_social}
                    />
                    <Dato label="Trade name" valor={pedido?.empresa} />
                    <Dato label="VAT/Tax ID" valor={pedido?.vat_tax_id} />
                    <Dato
                      label="Billing email"
                      valor={pedido?.correo_facturacion || pedido?.correo}
                    />
                  </dl>
                </>
              ) : null}
            </div>

            <div className="omx-card">
              <div className="omx-card-head">
                <span className="omx-card-title">Ship to</span>
              </div>

              <dl className="omx-datos">
                <Dato label="Recipient" valor={pedido?.nombre_destinatario} />
                <Dato label="Address 1" valor={pedido?.direccion_linea_1} />
                <Dato label="Address 2" valor={pedido?.direccion_linea_2} />
                <Dato label="City" valor={pedido?.ciudad} />
                <Dato label="State / Province" valor={pedido?.estado_provincia} />
                <Dato label="Country" valor={pedido?.pais} />
                <Dato label="Postal code" valor={pedido?.codigo_postal} />
              </dl>
            </div>

            <div className="omx-card">
              <div className="omx-card-head">
                <span className="omx-card-title">Shipping</span>
              </div>

              <dl className="omx-datos">
                <Dato label="Method" valor={pedido?.nombre_metodo_envio} />
                <Dato label="Carrier" valor={pedido?.metodo_envio_compania} />
                <Dato label="Account #" valor={pedido?.metodo_envio_no_cuenta} />
                <Dato label="PO #" valor={pedido?.no_po} />
                <Dato label="Contact" valor={pedido?.nombre_contacto} />
              </dl>
            </div>

            <div className="omx-card">
              <div className="omx-card-head">
                <span className="omx-card-title">Dates</span>
              </div>

              <dl className="omx-datos">
                <Dato label="Created" valor={formatDate(pedido?.fecha_creado)} />
                <Dato label="Shipped" valor={formatDate(pedido?.fecha_enviado)} />
                <Dato label="Delivered" valor={formatDate(pedido?.fecha_entregado)} />
              </dl>
            </div>
          </div>

          {/* ---------- Las lineas, a todo el ancho ---------- */}
          <div className="omx-card omx-card--items">
            <div className="omx-card-head">
              <span className="omx-card-title">
                Items
                <span className="omx-card-nota">
                  {items.length === 1 ? "1 line" : `${items.length} lines`}
                </span>
              </span>

              {/* Una etiqueta por producto de la orden, con la cantidad
                  comprada. Se desactiva si ninguno tiene codigo: los
                  productos cargados antes del codigo de barras no lo
                  tienen y no hay nada que imprimir. */}
              <button
                type="button"
                className="omx-btn-primary"
                onClick={() => setVerEtiquetas(true)}
                disabled={conCodigoDeBarras === 0}
                title={
                  conCodigoDeBarras === 0
                    ? "None of these products has a barcode"
                    : "Print one label per product"
                }
              >
                Print labels
              </button>
            </div>

            <div className="omx-tableWrap">
              <table className="omx-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="omx-right">Unit</th>
                    <th className="omx-right">Core</th>
                    <th className="omx-right">Weight</th>
                    <th className="omx-right">Qty</th>
                    <th className="omx-right">Core subtotal</th>
                    <th className="omx-right">Subtotal</th>
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="omx-empty">
                        {loading ? "Loading..." : "No items found."}
                      </td>
                    </tr>
                  ) : (
                    items.map((d, idx) => {
                      const rawImg = d?.url_image;
                      const imgSrc = rawImg ? fileURL(rawImg) : null;

                      const coreLine = calcCoreLine(d);
                      const coreUnit = getCoreUnit(d);

                      const key =
                        d?.pedido_detalles_id ?? `${d?.producto_id ?? "p"}-${idx}`;

                      return (
                        <tr key={key}>
                          <td className="omx-prod">
                            <div className="omx-prodRow">
                              <div className="omx-thumb">
                                {imgSrc ? (
                                  <img
                                    src={imgSrc}
                                    alt={d.nombre_producto || "Product"}
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="omx-thumbFallback">IMG</div>
                                )}
                              </div>

                              <div className="omx-prodTxt">
                                <div
                                  className="omx-prodName"
                                  title={d.nombre_producto || ""}
                                >
                                  {d.nombre_producto || "—"}
                                </div>
                                <div className="omx-prodMeta">
                                  {d.no_parte ? `P/N ${d.no_parte}` : null}
                                  {d.no_parte && d.numero_serie ? " · " : null}
                                  {d.numero_serie ? `S/N ${d.numero_serie}` : null}
                                  {!d.no_parte && !d.numero_serie
                                    ? `Product ID ${d.producto_id ?? "—"}`
                                    : null}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="omx-right">{money(d.precio_unitario)}</td>
                          <td className="omx-right">{fmtCore(coreUnit)}</td>
                          <td className="omx-right">{formatItemWeight(d)}</td>
                          <td className="omx-right">{d.cantidad ?? "—"}</td>
                          <td className="omx-right">
                            {coreLine === null ? "—" : money(coreLine)}
                          </td>
                          <td className="omx-right omx-strong">
                            {money(d.subtotal ?? d.total)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="omx-footer">
          <button className="omx-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {verEtiquetas ? (
        <BarcodeLabelsModal
          isOpen={verEtiquetas}
          onClose={() => setVerEtiquetas(false)}
          productos={productosParaEtiquetas}
          modo="pedido"
        />
      ) : null}
    </div>
  );
}

export default OrderViewModal;
